import { useSearchParams } from "react-router-dom";
import { trpc } from "../lib/trpc";

function fmtDateTime(d: Date | string | null): string {
  return d ? new Intl.DateTimeFormat("tr-TR", { dateStyle: "short", timeStyle: "short" }).format(new Date(d)) : "—";
}
const nf = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 4 });
/** Fark: işaret AÇIKÇA yazılır (+/−) — muhasebe/sayım belgesinde yön okunmadan anlaşılmamalı. */
function fmtDiff(n: number): string {
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${nf.format(Math.abs(n))}`;
}

/**
 * SAYIM TUTANAĞI (A4) — sayan kişi, tarih, kalem listesi, sistem/sayılan/fark ve imza.
 *
 * Ekranla AYNI ucu (`countReport`) okur: belge ile ekran ayrı sorgudan beslenirse biri
 * güncellenip diğeri unutulur ve imzalanan kâğıt ekrandakinden farklı olur.
 *
 * Satırlar snapshot: kart adı sonradan değişse bile bu belge sayım anındaki adı gösterir.
 */
export function StockCountPrintPage() {
  const [params] = useSearchParams();
  const id = params.get("id") ?? "";
  const q = trpc.inventory.countReport.useQuery({ id }, { enabled: !!id });
  const d = q.data;

  const toplamFark = (d?.lines ?? []).reduce((s, l) => s + Math.abs(l.diff), 0);
  const fazla = (d?.lines ?? []).filter((l) => l.diff > 0).length;
  const eksik = (d?.lines ?? []).filter((l) => l.diff < 0).length;

  return (
    <div className="scp-root">
      <style>{`
        .scp-root { --ink:#14253E; --line:#c9d2df; --muted:#5b6472; --soft:#f4f6fa; background:#fff; color:#1a2230; }
        .scp-page { max-width: 820px; margin: 0 auto; padding: 22px 26px 40px; font-family: 'Montserrat', system-ui, sans-serif; font-size: 12px; }
        .scp-num { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .scp-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; border-bottom: 3px solid var(--ink); padding-bottom: 12px; }
        .scp-title { font-size: 18px; font-weight: 800; color: var(--ink); letter-spacing:.02em; }
        .scp-sub { font-size: 11px; color: var(--muted); margin-top: 3px; }
        .scp-doc { text-align:right; }
        .scp-doc .c { font-size: 15px; font-weight: 800; color: var(--ink); font-family:'JetBrains Mono',monospace; }
        .scp-doc .s { font-size: 10.5px; margin-top: 3px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; }
        .scp-doc .s.open { color:#B45309; }
        .scp-doc .s.closed { color:#15803D; }
        .scp-meta { display:grid; grid-template-columns: repeat(4, 1fr); border:1px solid var(--line); border-top:none; }
        .scp-meta > div { padding: 8px 12px; }
        .scp-meta > div + div { border-left:1px solid var(--line); }
        .scp-meta .k { font-size: 9px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color: var(--muted); }
        .scp-meta .v { font-size: 12px; font-weight:600; color: var(--ink); margin-top:2px; }
        table.scp-t { width:100%; border-collapse: collapse; margin-top: 14px; }
        table.scp-t th { background: var(--soft); border:1px solid var(--line); padding: 6px 8px; font-size: 9.5px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; color: var(--muted); text-align:left; }
        table.scp-t td { border:1px solid var(--line); padding: 6px 8px; font-size: 11.5px; vertical-align: top; }
        table.scp-t td.n, table.scp-t th.n { text-align:right; font-family:'JetBrains Mono',monospace; font-variant-numeric: tabular-nums; }
        .scp-pos { color:#15803D; font-weight:700; }
        .scp-neg { color:#B91C1C; font-weight:700; }
        .scp-code { font-family:'JetBrains Mono',monospace; font-size:10px; color:var(--muted); display:block; }
        .scp-applied { font-size: 9.5px; color: var(--muted); font-family:'JetBrains Mono',monospace; }
        .scp-sum { display:flex; gap:22px; margin-top:10px; padding:8px 12px; border:1px solid var(--line); background:var(--soft); font-size:11.5px; }
        .scp-sum b { color: var(--ink); }
        .scp-note { margin-top:12px; padding:8px 12px; border:1px solid var(--line); font-size:11px; color:#37414f; }
        .scp-sign { display:grid; grid-template-columns: repeat(3, 1fr); gap: 26px; margin-top: 34px; }
        .scp-sign .b { border-top:1px solid var(--ink); padding-top:6px; font-size:10.5px; color:var(--muted); min-height:56px; }
        .scp-sign .b b { display:block; color:var(--ink); font-size:11.5px; margin-bottom:2px; }
        .scp-foot { margin-top: 18px; font-size: 9.5px; color: var(--muted); text-align:center; }
        .scp-print { position:fixed; top:14px; right:16px; }
        @media print {
          .scp-print { display:none; }
          .scp-page { max-width:none; padding: 0; }
          table.scp-t { page-break-inside: auto; }
          table.scp-t tr { page-break-inside: avoid; }
          thead { display: table-header-group; }
        }
      `}</style>

      <button type="button" className="btn-md primary scp-print" onClick={() => window.print()}>Yazdır</button>

      <div className="scp-page">
        {!id ? (
          <p>Tutanak seçilmedi.</p>
        ) : q.isLoading ? (
          <p>Yükleniyor…</p>
        ) : !d ? (
          <p>Tutanak bulunamadı.</p>
        ) : (
          <>
            <div className="scp-head">
              <div>
                <div className="scp-title">STOK SAYIM TUTANAĞI</div>
                <div className="scp-sub">{d.facilityName ?? "Tesis belirtilmemiş"}</div>
              </div>
              <div className="scp-doc">
                <div className="c">{d.code}</div>
                {/* Durum = renk + METİN (renk tek başına anlam taşımaz). */}
                <div className={`s ${d.status === "open" ? "open" : "closed"}`}>
                  {d.status === "open" ? "AÇIK — SAYIM SÜRÜYOR" : "KAPALI"}
                </div>
              </div>
            </div>

            <div className="scp-meta">
              <div><div className="k">Sayan</div><div className="v">{d.countedBy}</div></div>
              <div><div className="k">Başlangıç</div><div className="v scp-num">{fmtDateTime(d.startedAt)}</div></div>
              <div><div className="k">Kapanış</div><div className="v scp-num">{fmtDateTime(d.closedAt)}</div></div>
              <div><div className="k">Kapatan</div><div className="v">{d.closedBy ?? "—"}</div></div>
            </div>

            <table className="scp-t">
              <thead>
                <tr>
                  <th style={{ width: 26 }} className="n">#</th>
                  <th>ÜRÜN</th>
                  <th className="n" style={{ width: 92 }}>SİSTEM</th>
                  <th className="n" style={{ width: 92 }}>SAYILAN</th>
                  <th className="n" style={{ width: 92 }}>FARK</th>
                  <th style={{ width: 44 }}>BİRİM</th>
                  <th style={{ width: 150 }}>UYGULANDIĞI LOT</th>
                </tr>
              </thead>
              <tbody>
                {d.lines.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--muted)", padding: 18 }}>Bu tutanakta kayıtlı fark yok.</td></tr>
                ) : d.lines.map((l, i) => (
                  <tr key={l.id}>
                    <td className="n">{i + 1}</td>
                    <td>
                      {l.itemName}
                      <span className="scp-code">{l.itemCode}</span>
                    </td>
                    <td className="n">{nf.format(l.systemQty)}</td>
                    <td className="n">{nf.format(l.countedQty)}</td>
                    <td className={`n ${l.diff > 0 ? "scp-pos" : l.diff < 0 ? "scp-neg" : ""}`}>{fmtDiff(l.diff)}</td>
                    <td>{l.uom}</td>
                    <td className="scp-applied">{l.appliedTo ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="scp-sum">
              <span>Kalem: <b>{d.lines.length}</b></span>
              <span>Fazla çıkan: <b>{fazla}</b></span>
              <span>Eksik çıkan: <b>{eksik}</b></span>
              <span>Toplam mutlak fark: <b className="scp-num">{nf.format(toplamFark)}</b></span>
            </div>

            {d.notes ? <div className="scp-note"><b>Not:</b> {d.notes}</div> : null}

            <div className="scp-sign">
              <div className="b"><b>Sayımı Yapan</b>{d.countedBy}</div>
              <div className="b"><b>Kontrol Eden</b>&nbsp;</div>
              <div className="b"><b>Onaylayan</b>&nbsp;</div>
            </div>

            <div className="scp-foot">
              Bu tutanak sayım anındaki sistem ve sayılan miktarları gösterir; sonradan yapılan
              stok hareketleri bu belgeyi değiştirmez.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
